// Single source of truth for subscription "kind" labels and predicates.
// Kind VALUES are wire literals and stay snake_case (price_below, price_pct_drop, content_change).
import type { ContentSubscriptionKind, PriceSubscriptionKind, SubscriptionKind } from '@/types'

const CONTENT_SUBSCRIPTION_KIND: ContentSubscriptionKind = 'content_change'

const PERCENT_SUBSCRIPTION_KINDS: readonly PriceSubscriptionKind[] = ['price_pct_drop', 'price_pct_rise']

export function isContentKind(kind: SubscriptionKind): kind is ContentSubscriptionKind {
  return kind === CONTENT_SUBSCRIPTION_KIND
}

export function isPriceKind(kind: SubscriptionKind): kind is PriceSubscriptionKind {
  return kind !== CONTENT_SUBSCRIPTION_KIND
}

export function isPercentKind(kind: SubscriptionKind): boolean {
  return (PERCENT_SUBSCRIPTION_KINDS as readonly SubscriptionKind[]).includes(kind)
}

const PRICE_KIND_LABELS: Record<PriceSubscriptionKind, string> = {
  price_below: 'Price below',
  price_above: 'Price above',
  price_pct_drop: 'Drop by',
  price_pct_rise: 'Rise by',
}

interface PriceAlertTypeOption {
  value: PriceSubscriptionKind
  label: string
  description: string
}

/** Full set of price-alert kinds, with descriptions, for the per-item alert modal. */
export const PRICE_ALERT_TYPE_OPTIONS: PriceAlertTypeOption[] = [
  { value: 'price_below', label: 'Price drops below', description: 'Get notified when price drops below a threshold' },
  { value: 'price_above', label: 'Price rises above', description: 'Get notified when price rises above a threshold' },
  {
    value: 'price_pct_drop',
    label: 'Price drops by %',
    description: 'Get notified when price drops by a percentage from current',
  },
  {
    value: 'price_pct_rise',
    label: 'Price rises by %',
    description: 'Get notified when price rises by a percentage from current',
  },
]

/** Percentage-only subset, for the bulk-create-from-buylist modal. */
export const PERCENT_ALERT_TYPE_OPTIONS: PriceAlertTypeOption[] = PRICE_ALERT_TYPE_OPTIONS.filter((o) =>
  isPercentKind(o.value),
)

export function subscriptionKindLabel(kind: SubscriptionKind): string {
  if (isContentKind(kind)) return 'Content change'
  return PRICE_KIND_LABELS[kind] ?? kind
}

/** Formats a threshold value according to the kind (percentage vs currency). */
export function formatThreshold(kind: SubscriptionKind, value: number, formatPrice: (v: number) => string): string {
  return isPercentKind(kind) ? `${value}%` : formatPrice(value)
}
